/**
 * Reasons to come back each day and each week (D304): three village orders a day, a gift on the
 * first visit each day, and a themed week with a prize. All pure: the state and the server's
 * clock decide everything. Days run from midnight UTC.
 *
 * The orders and the gift are settled on a check-in (every action checks in first), never in a
 * bare advance, as goals are. Each pays seconds of the world's income, as a lucky find does (D254),
 * and is paid while the player is here, so none of it counts as away earnings. A day's rewards
 * together never come near a full barn (D242). A missed order or a missed day simply goes.
 *
 * D331: the gift, all of a day's orders sent, and the week's event won also pay Trimmings.
 */
import type { IdleContent } from '@hedgerow/data';
import { noteIdleCollection } from './collection.ts';
import { idleEffects, idleProducerStats } from './effects.ts';
import { checkInIdle, idleIncome, type IdleResult } from './world.ts';
import { idleFillLineage } from './lineage.ts';
import { idleChance, idleSeed, LUCK_ORDERS } from './luck.ts';
import { idleClamp } from './rules.ts';
import { idleFoldRosetteWeek } from './rosettes.ts';
import type { IdleDailyState, IdleWorldState, IdleOrderState } from './state.ts';
import { idleEarnTrimmings } from './trimmings.ts';

export type IdleEventT = IdleContent['events'][number];

/** The day number at `t`: whole days since 1 January 1970, UTC. */
export function idleDay(content: IdleContent, t: number): number {
  return Math.floor(t / content.daily.dayMs);
}

/** When the day after the one at `t` begins: the next orders and the next gift. */
export function idleNextDayAt(content: IdleContent, t: number): number {
  return (idleDay(content, t) + 1) * content.daily.dayMs;
}

/** The week number of a day. */
export function idleWeek(content: IdleContent, day: number): number {
  return Math.floor((day - content.daily.weekStartDay) / 7);
}

/** The themed week running in a week, in turn from the content. */
export function idleEventFor(content: IdleContent, week: number): IdleEventT {
  const n = content.events.length;
  return content.events[((week % n) + n) % n]!;
}

/** The run of days with a visit, counting a visit on `day`. 0 on the day the world was made. */
function streakOn(state: IdleWorldState, content: IdleContent, day: number): number {
  if (idleDay(content, state.createdAt) >= day) return 0;
  const d = state.daily;
  return d && d.day === day - 1 ? d.streak + 1 : 1;
}

/** True when a check-in at `now` is the first visit of a new day. */
function newDay(state: IdleWorldState, content: IdleContent, now: number): boolean {
  const d = state.daily;
  return !d || idleDay(content, now) > d.day;
}

export interface IdleGift {
  coins: number;
  /** Days in a row, today counted. */
  streak: number;
  /** D331: Trimmings it pays. */
  trimmings: number;
}

/**
 * The gift a check-in at `now` would pay: on the first visit of a day, never on the day the world
 * was made, and never while a festival waits (D276). Pass the world advanced to `now`.
 */
export function idleGiftDue(
  state: IdleWorldState,
  content: IdleContent,
  now: number,
): IdleGift | undefined {
  if (state.festivalPending || !newDay(state, content, now)) return undefined;
  const streak = streakOn(state, content, idleDay(content, now));
  if (streak < 1) return undefined;
  const g = content.daily.gift;
  const worth = g.worthSeconds[Math.min(streak, g.worthSeconds.length) - 1]!;
  const t = content.trimmings.gift;
  const trimmings = t[Math.min(streak, t.length) - 1]!;
  return { coins: worthOf(state, content, now, worth, g.minCoins), streak, trimmings };
}

/** `seconds` of the world's income at `now`, tapped bars counted, and at least `min`. */
function worthOf(
  state: IdleWorldState,
  content: IdleContent,
  now: number,
  seconds: number,
  min: number,
): number {
  return Math.max(min, Math.floor(idleIncome(state, content, now, true) * seconds));
}

/** A round number to ask for: whole below ten, two figures above. */
function roundCount(n: number): number {
  if (n < 10) return Math.max(1, Math.ceil(n));
  const step = 10 ** (Math.floor(Math.log10(n)) - 1);
  return Math.round(n / step) * step;
}

/**
 * The day's orders, one for each size, from the day after the world was made. Each asks for a
 * different crop the world has unlocked where it has enough, picked by the world's seed and the
 * day; the larger orders go to the later crops. Each asks for what its size's seconds of making
 * would make now.
 */
function dealOrders(
  state: IdleWorldState,
  content: IdleContent,
  day: number,
  now: number,
): IdleOrderState[] {
  // None on the day the world was made: a new world grows too fast for an order to mean anything.
  if (idleDay(content, state.createdAt) >= day) return [];
  const pool = content.producers.map((p) => p.id).filter((id) => state.year.producers[id]);
  if (pool.length === 0) return [];
  const sizes = content.daily.orders.sizes;
  const seed = idleSeed(state);
  const order = [...pool];
  for (let i = order.length - 1; i > 0; i--) {
    const j = Math.floor(idleChance(seed, LUCK_ORDERS, day * 64 + i) * (i + 1));
    [order[i], order[j]] = [order[j]!, order[i]!];
  }
  // The crops picked, back in content order, so the larger orders fall on the later crops.
  const picked = order.slice(0, sizes.length).sort((a, b) => pool.indexOf(a) - pool.indexOf(b));
  const fx = idleEffects(state, content, now);
  return sizes.map((size, i) => {
    const producer = picked[Math.min(i, picked.length - 1)]!;
    const owned = state.year.producers[producer]!.owned;
    const { cycleMs } = idleProducerStats(state, content, producer, fx);
    return { producer, count: roundCount(((owned * 1000) / cycleMs) * size.seconds) };
  });
}

/** What an order pays when filled at `now`: its size's seconds of the world's income. */
export function idleOrderCoins(
  state: IdleWorldState,
  content: IdleContent,
  index: number,
  now: number,
): number {
  const o = content.daily.orders;
  const size = o.sizes[Math.min(index, o.sizes.length - 1)]!;
  return worthOf(state, content, now, size.worthSeconds, o.minCoins);
}

/** Coins paid while the player is here. The check-in moves `earnedAtSeen` after. */
function pay(state: IdleWorldState, coins: number): IdleWorldState {
  const y = state.year;
  const p = state.permanent;
  return {
    ...state,
    // D316: held below IDLE_MAX, never Infinity.
    year: { ...y, coins: idleClamp(y.coins + coins), earned: idleClamp(y.earned + coins) },
    permanent: { ...p, lifetimeEarned: idleClamp(p.lifetimeEarned + coins) },
  };
}

/**
 * Note what the producers have made towards the day's orders: units, a bar of each one owned
 * being one. Returns the same state when there are no orders or nothing was made.
 */
export function idleNoteMade(state: IdleWorldState, made: Record<string, number>): IdleWorldState {
  const d = state.daily;
  if (!d) return state;
  const add = Object.entries(made).filter(([, n]) => n > 0);
  if (add.length === 0) return state;
  const next = { ...d.made };
  for (const [id, n] of add) next[id] = (next[id] ?? 0) + n;
  return { ...state, daily: { ...d, made: next } };
}

/** How far the week's event goal has got, in its own units (orders or days). */
function eventProgress(d: IdleDailyState | undefined, event: IdleEventT, week: number): number {
  if (!d || d.week !== week) return 0;
  return event.goal.kind === 'orders' ? d.weekOrders : d.weekDays;
}

/** True when an order has all it asks for and is not yet sent. */
function orderReady(d: IdleDailyState, o: IdleOrderState): boolean {
  return !o.done && (d.made[o.producer] ?? 0) >= o.count;
}

/** Pay order `i` of the day and count it towards the week. */
function sendOrder(
  state: IdleWorldState,
  content: IdleContent,
  d: IdleDailyState,
  i: number,
  now: number,
): { s: IdleWorldState; d: IdleDailyState } {
  const paid = pay(state, idleOrderCoins(state, content, i, now));
  const orders = d.orders.map((x, j) => (j === i ? { ...x, done: true } : x));
  // D331: the last of the day's orders sent pays Trimmings, once.
  const all = orders.every((o) => o.done);
  const s = all ? idleEarnTrimmings(paid, content.trimmings.allOrders) : paid;
  return { s, d: { ...d, orders, weekOrders: d.weekOrders + 1 } };
}

/** Store `d`, and give the week's prize once its goal is met. */
function winEvent(
  s: IdleWorldState,
  content: IdleContent,
  d: IdleDailyState,
  now: number,
): IdleWorldState {
  const event = idleEventFor(content, d.week);
  if (d.eventWon || eventProgress(d, event, d.week) < event.goal.count) return { ...s, daily: d };
  const won = { ...d, eventWon: true };
  // D331: the week's event won pays Trimmings, once.
  const paid = idleEarnTrimmings({ ...s, daily: won }, content.trimmings.event);
  return noteIdleCollection(paid, content, now, undefined, event.prize.id);
}

/**
 * Settle the day on a check-in at `now`. On the first visit of a day: send any order of the day
 * before that was filled and not sent, so nothing is lost; pay `gift`; and deal new orders (the
 * old ones go). Then give the week's prize once its goal is met. `gift` is `idleGiftDue` of the
 * world advanced to `now`. Not run while a festival waits. An order is sent by `sendIdleOrder`.
 */
export function settleIdleDaily(
  state: IdleWorldState,
  content: IdleContent,
  now: number,
  gift: IdleGift | undefined,
): IdleWorldState {
  if (state.festivalPending) return state;
  const day = idleDay(content, now);
  const week = idleWeek(content, day);
  let s = state;
  let d = s.daily;
  if (newDay(s, content, now)) {
    if (d) {
      for (let i = 0; i < d.orders.length; i++)
        if (orderReady(d, d.orders[i]!)) ({ s, d } = sendOrder(s, content, d, i, now));
      s = winEvent(s, content, d, now);
      d = s.daily!;
      // D326: the week just ended (or more than one, if the world was away) folds into the
      // rolling window towards the next rosette; `s.daily` is untouched by the fold.
      if (d.week !== week) {
        s = idleFoldRosetteWeek(s, content, !!d.eventWon);
        for (let w = d.week + 1; w < week; w++) s = idleFoldRosetteWeek(s, content, false);
      }
    }
    const same = d?.week === week;
    d = {
      day,
      orders: dealOrders(s, content, day, now),
      made: {},
      streak: streakOn(s, content, day),
      week,
      weekOrders: same ? d!.weekOrders : 0,
      weekDays: (same ? d!.weekDays : 0) + 1,
      ...(same && d!.eventWon ? { eventWon: true } : {}),
    };
    if (gift) s = idleEarnTrimmings(pay(s, gift.coins), gift.trimmings);
  }
  if (!d) return s;
  return winEvent(s, content, d, now);
}

/** D304: send order `index` of the day once it has all it asks for, and be paid for it. */
export function sendIdleOrder(
  state: IdleWorldState,
  content: IdleContent,
  index: number,
  now: number,
): IdleResult {
  const s = checkInIdle(state, content, now);
  const d = s.daily;
  const o = d && d.day === idleDay(content, now) ? d.orders[index] : undefined;
  if (!d || !o || o.done) return { ok: false, reason: 'no_order' };
  if (!orderReady(d, o)) return { ok: false, reason: 'order_not_ready' };
  const sent = sendOrder(s, content, d, index, now);
  // D325: an order sent adds a little to the Lineage meter.
  const paid = idleFillLineage(winEvent(sent.s, content, sent.d, now), content, 'order');
  // Paid while here: never counted as away earnings.
  return { ok: true, state: { ...paid, earnedAtSeen: paid.permanent.lifetimeEarned } };
}

export interface IdleOrderView {
  producer: string;
  /** What is sent: "eggs", or the producer's own name. */
  goods: string;
  count: number;
  /** Made towards it so far, at most `count`. */
  made: number;
  done: boolean;
  /** Has all it asks for, and waits to be sent. */
  ready: boolean;
  /** What it pays if filled now; what it paid once done. */
  coins: number;
}

/** The day's orders at `now`. Empty until the day's first check-in has dealt them. */
export function idleOrders(
  state: IdleWorldState,
  content: IdleContent,
  now: number,
): IdleOrderView[] {
  const d = state.daily;
  if (!d || d.day !== idleDay(content, now)) return [];
  return d.orders.map((o, i) => {
    const name = content.producers.find((p) => p.id === o.producer)?.name ?? o.producer;
    return {
      producer: o.producer,
      goods: content.daily.orders.goods[o.producer] ?? name.toLowerCase(),
      count: o.count,
      made: Math.min(o.count, d.made[o.producer] ?? 0),
      done: !!o.done,
      ready: orderReady(d, o),
      coins: idleOrderCoins(state, content, i, now),
    };
  });
}

export interface IdleEventView {
  event: IdleEventT;
  /** Orders filled or days visited this week, at most the goal. */
  progress: number;
  goal: number;
  won: boolean;
  /** When the week, and its event, ends. */
  endsAt: number;
}

/** The themed week running at `now`, and how far this world has got with its goal. */
export function idleEventNow(
  state: IdleWorldState,
  content: IdleContent,
  now: number,
): IdleEventView {
  const week = idleWeek(content, idleDay(content, now));
  const event = idleEventFor(content, week);
  const d = state.daily;
  const won = !!d && d.week === week && !!d.eventWon;
  return {
    event,
    progress: Math.min(event.goal.count, eventProgress(d, event, week)),
    goal: event.goal.count,
    won,
    endsAt: ((week + 1) * 7 + content.daily.weekStartDay) * content.daily.dayMs,
  };
}
